import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import './LoginPage.css';
import logo from "../assets/logo.png";

const ALLOWED_DOMAIN = 'hyderabad.bits-pilani.ac.in';

const ERROR_MESSAGES = {
  NOT_IN_WHITELIST: {
    title: 'Unauthorized User',
    body:  'Your account is not in the approved list. Please contact your administrator to get access.',
    icon:  '🚫',
  },
  USER_DEACTIVATED: {
    title: 'Account Deactivated',
    body:  'Your access has been deactivated. Please contact your administrator.',
    icon:  '🔒',
  },
  WRONG_DOMAIN: {
    title: 'Wrong Account',
    body:  `Only @${ALLOWED_DOMAIN} accounts are allowed. Please sign in with your BITS Hyderabad email.`,
    icon:  '⚠️',
  },
  DEFAULT: {
    title: 'Access Denied',
    body:  'You are not authorized to access this system.',
    icon:  '🚫',
  },
};

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [authError, setAuthError] = useState(null);  // { title, body, icon }

  useEffect(() => {
    if (user) {
      navigate(user.role === 'user' ? '/feedback' : '/admin/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleGoogleSuccess = async (credentialResponse) => {
    setAuthError(null);
    try {
      const u = await login(credentialResponse.credential);
      toast.success(`Welcome, ${u.name}!`);
      navigate(u.role === 'user' ? '/feedback' : '/admin/dashboard', { replace: true });
    } catch (err) {
      if (err.response?.status === 403) {
        const code = err.response?.data?.code || 'DEFAULT';
        setAuthError(ERROR_MESSAGES[code] || ERROR_MESSAGES.DEFAULT);
      } else {
        toast.error(err.response?.data?.error || 'Authentication failed');
      }
    }
  };

  return (
    <div className="login-page">
      <div className="login-bg">
        <div className="bg-orb orb-1" />
        <div className="bg-orb orb-2" />
        <div className="bg-orb orb-3" />
      </div>

      <div className="login-card animate-scale">
        <div className="login-header">
          <div className="login-logo">
            <img src={logo} alt="Logo" />
          </div>
          <h1 className="login-title font-Arial">Semester Feedback</h1>
          <h6 className="login-title1 font-Arial">Administrative & Support service<br />(2nd Sem 2025-26)</h6>
          <p className="login-subtitle">BITS Pilani, Hyderabad Campus</p>
        </div>

        {/* Unauthorized error box — shown after failed login */}
        {authError ? (
          <div className="login-auth-error animate-fade">
            <div className="login-auth-error-icon">{authError.icon}</div>
            <div className="login-auth-error-body">
              <strong>{authError.title}</strong>
              <p>{authError.body}</p>
            </div>
            <button
              className="login-auth-retry"
              onClick={() => setAuthError(null)}
            >
              Try a different account
            </button>
          </div>
        ) : (
          <>
            <div className="login-divider">
              <span>Sign in with your BITS account</span>
            </div>

            <div className="login-domain-notice">
              <span>🏛️</span>
              <span>Use your <strong>@{ALLOWED_DOMAIN}</strong> account</span>
            </div>

            <div className="login-google">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => toast.error('Google sign-in failed. Please try again.')}
                useOneTap
                shape="rectangular"
                size="large"
                text="signin_with"
                theme="outline"
                hosted_domain={ALLOWED_DOMAIN}
              />
            </div>

            <p className="login-footer">
              &copy; 2026 All Rights Reserved. <br />Credits to <a href="https://www.linkedin.com/in/narasimhapuli/" target='_blank'>V L Narasimha Rao PULI</a><br /><strong>Digital Applications Development Unit</strong> <br />BITS Pilani, Hyderabad Campus.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
